/**
 * Dashboard Page
 * Main dashboard with KPIs, action panel, upcoming services, and chart
 */

import { useNavigate } from 'react-router-dom'
import {
  Package,
  Users,
  DollarSign,
  FileText,
  PlusCircle,
  Briefcase,
  CheckCircle2,
  Percent,
} from 'lucide-react'
import { KPICard } from '@/components/dashboard/KPICard'
import { OrderOverview } from '@/components/dashboard/OrderOverview'
import { ServicesChart } from '@/components/dashboard/ServicesChart'
import { WeekComparisonChart } from '@/components/dashboard/WeekComparisonChart'
import { ServicePipelineChart } from '@/components/dashboard/ServicePipelineChart'
import { TopBreakdownChart } from '@/components/dashboard/TopBreakdownChart'
import { ActionPanel } from '@/components/dashboard/ActionPanel'
import { DashboardHeader } from '@/components/dashboard/DashboardHeader'
import {
  useDashboardStats,
  useWeeklyServices,
  useWeeklyPerformance,
} from '@/hooks/useDashboardStats'
import type { WeeklyKpiDelta } from '@/types/dashboard'
import { cn } from '@/lib/utils'
import { useServiceActions } from '@/hooks/useServiceActions'
import { useOrganization } from '@/contexts/OrganizationContext'
import { useProfileStore } from '@/store/profileStore'
import { useAuthStore } from '@/store/authStore'
import { Skeleton } from '@/components/ui/skeleton'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Button } from '@/components/ui/button'
import { useLocalization } from '@/contexts/LocalizationContext'
import { currencySymbol } from '@/utils/formatters'

const ENTITY_TYPES = ['ENTITY', 'ORGANIZATION']

function useIsBusinessClientDashboard(): boolean {
  const profileMode = useProfileStore((s) => s.activeMode)
  const user = useAuthStore((s) => s.user)
  if (profileMode === 'business_client') return true
  const profiles = user?.profiles ?? []
  const hasEntity = profiles.some((p) => ENTITY_TYPES.includes(p.profile_type))
  const hasBC = profiles.some((p) => p.profile_type === 'BUSINESS_CLIENT')
  return !hasEntity && hasBC
}

/**
 * Turn a {current, previous} pair into the trend pill KPICard expects.
 * Returns undefined (no pill) when there's nothing to compare against.
 */
function computeTrend(
  delta?: WeeklyKpiDelta,
): { value: number; isPositive: boolean } | undefined {
  if (!delta) return undefined
  const { current, previous } = delta
  if (previous === 0) {
    if (current === 0) return undefined
    return { value: 100, isPositive: true }
  }
  const pct = Math.round(((current - previous) / previous) * 100)
  return { value: Math.abs(pct), isPositive: pct >= 0 }
}

export function DashboardPage() {
  const navigate = useNavigate()
  const { currentOrganization } = useOrganization()
  const { t } = useLocalization()
  const isBC = useIsBusinessClientDashboard()
  
  const {
    stats,
    isLoading,
    isError,
    error,
  } = useDashboardStats()

  const {
    weeklyServices,
    isLoading: weeklyLoading,
    isFetching: weeklyFetching,
    refetch: refetchWeekly,
  } = useWeeklyServices()

  const { performance, isLoading: perfLoading } = useWeeklyPerformance()
  
  const {
    entityAssignProvider,
    declineService,
    acceptService,
    isLoading: isActionLoading,
  } = useServiceActions()

  // Business client mode: show a simplified, focused dashboard
  if (isBC) {
    return <BusinessClientDashboard />
  }

  // Loading state
  if (isLoading) {
    return <DashboardSkeleton />
  }

  const formatCurrencyValue = (value: number, currency: string = 'SAR') => {
    const formatted = new Intl.NumberFormat('en-SA', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value)
    return `${currencySymbol(currency)} ${formatted}`
  }

  if (isError) {
    return (
      <div className="container mx-auto px-4 py-6 space-y-6">
        <div className="flex items-center justify-between gap-4">
          <DashboardHeader />
        </div>

        <Alert variant="destructive">
          <AlertDescription>
            {t('dashboard.failedToLoadDashboard')} {(error as Error)?.message || t('common.unknownError')}
          </AlertDescription>
        </Alert>
      </div>
    )
  }

  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header Section */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <DashboardHeader />
      </div>

      {/* KPI Cards Row - 4 cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <KPICard
          title={t('dashboard.totalOrders')}
          value={stats?.orderStatistics?.totalOrders ?? 0}
          icon={Package}
          description={t('dashboard.allOrders')}
          onClick={() => navigate('/orders')}
        />
        <KPICard
          title={t('dashboard.activeProviders')}
          value={stats?.activeProvidersCount ?? 0}
          icon={Users}
          description={t('dashboard.statusActive')}
          onClick={() => navigate('/providers')}
        />
        <KPICard
          title={t('dashboard.monthlyRevenue')}
          value={formatCurrencyValue(stats?.currentMonthRevenue ?? 0, stats?.revenueCurrency)}
          icon={DollarSign}
          description={t('dashboard.thisMonth')}
          onClick={() => navigate('/financials')}
        />
        <KPICard
          title={t('dashboard.orderReports')}
          value={stats?.orderStatistics?.completedOrders ?? 0}
          icon={FileText}
          description={t('dashboard.completedOrders')}
          onClick={() => navigate('/orders?status=completed')}
        />
      </div>

      {/* Action Required - Full Width */}
      <ActionPanel
        services={stats?.actionRequiredServices ?? []}
        loading={isLoading || isActionLoading}
        onAssign={(serviceId, providerProfileId) =>
          entityAssignProvider(serviceId, { provider_profile_id: providerProfileId })
        }
        onDecline={declineService}
        onAccept={acceptService}
      />

      {/* Upcoming Services - Full Width */}
      <OrderOverview
        orders={stats?.upcomingServices ?? []}
        onViewAll={() => navigate('/orders')}
      />

      {/* Performance Strip - this week vs last week */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <KPICard
          title={t('dashboard.servicesCompleted')}
          value={performance?.kpis.completed.current ?? 0}
          icon={CheckCircle2}
          variant="compact"
          description={t('dashboard.vsLastWeek')}
          trend={computeTrend(performance?.kpis.completed)}
          loading={perfLoading}
        />
        <KPICard
          title={t('dashboard.completionRate')}
          value={`${performance?.kpis.completionRate.current ?? 0}%`}
          icon={Percent}
          variant="compact"
          description={t('dashboard.vsLastWeek')}
          trend={computeTrend(performance?.kpis.completionRate)}
          loading={perfLoading}
        />
      </div>


      {/* Week comparison + service pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <WeekComparisonChart
          thisWeekByDay={performance?.thisWeekByDay ?? []}
          lastWeekByDay={performance?.lastWeekByDay ?? []}
          loading={perfLoading}
        />
        <ServicePipelineChart data={performance?.statusBreakdown ?? []} loading={perfLoading} />
      </div>

      {/* Top breakdowns - services / providers (/ categories once backend ships the field) */}
      <div
        className={cn(
          'grid grid-cols-1 gap-6',
          (performance?.topServiceCategories?.length ?? 0) > 0
            ? 'md:grid-cols-3'
            : 'md:grid-cols-2',
        )}
      >
        <TopBreakdownChart
          title={t('dashboard.topServices')}
          data={performance?.topServices ?? []}
          loading={perfLoading}
        />
        <TopBreakdownChart
          title={t('dashboard.topProviders')}
          data={performance?.topProviders ?? []}
          color="#00A67F"
          loading={perfLoading}
          emptyMessage={t('dashboard.noProviderData')}
        />
        {(performance?.topServiceCategories?.length ?? 0) > 0 && (
          <TopBreakdownChart
            title={t('dashboard.byCategory')}
            data={performance?.topServiceCategories ?? []}
            color="#497BE8"
            loading={perfLoading}
          />
        )}
      </div>


      {/* Services Completed This Week - Full Width at Bottom */}
      <ServicesChart
        data={weeklyServices}
        loading={weeklyLoading}
        isRefreshing={weeklyFetching}
        onRefresh={() => refetchWeekly()}
        organizationName={currentOrganization?.name}
        isWeekly={true}
        title={t('dashboard.servicesCompletedThisWeek')}
        showInsights={false}
      />
    </div>
  )
}

/**
 * Simplified dashboard for Business Client mode.
 * No org-dependent data; focused on order creation.
 */
function BusinessClientDashboard() {
  const navigate = useNavigate()
  const { t } = useLocalization()
  const user = useAuthStore((s) => s.user)
  const bcProfile = user?.profiles?.find((p) => p.profile_type === 'BUSINESS_CLIENT')

  const getGreeting = () => {
    const hour = new Date().getHours()
    if (hour < 12) return t('dashboard.goodMorning')
    if (hour < 17) return t('dashboard.goodAfternoon')
    return t('dashboard.goodEvening')
  }

  const firstName = user?.first_name || user?.phone_number?.slice(-4) || t('common.user')

  return (
    <div className="container mx-auto px-4 py-6 space-y-8">
      {/* Header */}
      <div className="space-y-1">
        <h1 className="text-2xl lg:text-3xl font-bold tracking-tight">
          {getGreeting()}, {firstName}
        </h1>
        {bcProfile?.display_name && (
          <div className="flex items-center gap-2 text-sm text-amber-700">
            <Briefcase className="h-4 w-4" />
            <span className="font-medium">{bcProfile.display_name}</span>
          </div>
        )}
      </div>

      {/* Quick Actions */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        {/* Create Order Card */}
        <div
          onClick={() => navigate('/create-order')}
          className="group relative overflow-hidden rounded-2xl border bg-gradient-to-br from-brand-primary/5 to-brand-secondary/5 p-6 cursor-pointer transition-all duration-200 hover:shadow-lg hover:border-brand-primary/30"
        >
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-brand-primary to-brand-secondary text-white shadow-md group-hover:shadow-lg transition-shadow">
              <PlusCircle className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('nav.createOrder')}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {t('profileSwitch.businessClientDescription')}
              </p>
              <Button
                size="sm"
                className="mt-4 bg-gradient-to-r from-brand-primary to-brand-secondary text-white hover:opacity-90"
              >
                {t('orders.newOrder')}
              </Button>
            </div>
          </div>
        </div>

        {/* My Orders Card */}
        <div className="relative overflow-hidden rounded-2xl border bg-gradient-to-br from-amber-50/50 to-orange-50/50 p-6">
          <div className="flex items-start gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-gradient-to-br from-amber-500 to-orange-500 text-white shadow-md">
              <Package className="h-7 w-7" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-gray-900">
                {t('dashboard.totalOrders')}
              </h3>
              <p className="text-sm text-gray-500 mt-1">
                {t('dashboard.allOrders')}
              </p>
              <p className="text-3xl font-bold text-amber-700 mt-3">—</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

// ==================== Helper Components ====================

/**
 * Dashboard skeleton for loading state
 */
function DashboardSkeleton() {
  return (
    <div className="container mx-auto px-4 py-6 space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div className="space-y-2">
          <Skeleton className="h-8 w-64" />
          <Skeleton className="h-4 w-48" />
        </div>
        <Skeleton className="h-10 w-10" />
      </div>
      
      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[1, 2, 3, 4].map((i) => (
          <Skeleton key={i} className="h-32" />
        ))}
      </div>

      {/* Performance strip */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[1, 2].map((i) => (
          <Skeleton key={i} className="h-24" />
        ))}
      </div>

      {/* Comparison + pipeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Skeleton className="h-80" />
        <Skeleton className="h-80" />
      </div>

      {/* Top breakdowns */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Skeleton className="h-80" />
        <Skeleton className="h-80" />
      </div>

      {/* Action Required */}
      <Skeleton className="h-80" />
      
      {/* Upcoming Services */}
      <Skeleton className="h-96" />
      
      {/* Services Chart */}
      <Skeleton className="h-80" />
    </div>
  )
}

/**
 * Map order status to OrderOverview status type
 */
// mapOrderStatus removed: dashboard table now uses service rows directly
